import React, { useCallback, useMemo, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import { ModalModalityEnum } from "../../../../classes/enums/ModalModalityEnum";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import { formatDisplayDate } from "../../../../classes/utils/DateUtils";
import IconButton from "../../../../components/iconButton/IconButton";
import WidgetLoader from "../../../../components/widgetLoader/WidgetLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { useDateFormatter } from "../../../../hooks/useDateFormatter";
import { useRouteState } from "../../../../hooks/useRouteState";
import "./PersonalInformation.css";
import PersonalInformationEditModal from "./PersonalInformationEditModal";

const InfoItem = React.memo(({ label, value }) => (
  <div className="info-item App-text-small">
    <span className="label-text App-bold-text">{label}:</span>
    <span className="App-normal-text">{value || "-"}</span>
  </div>
));

const PersonalInformation = React.memo(() => {
  const { t } = useTranslation();
  const { showPopup, hidePopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const { patientData, patientLoading, patientError, refreshPatientContext } =
    usePatientContext();
  const { params } = useRouteState();
  const { dateSettings } = useUser();
  const [imageSrc, setImageSrc] = useState(null);
  const [imageError, setImageError] = useState(false);
  const formatDate = useDateFormatter();

  const canEdit = useMemo(() => {
    return checkPermissions([DataPermissions.UPDATE_PATIENT]);
  }, [checkPermissions]);

  const handlePersonalInfoEdit = useCallback(() => {
    refreshPatientContext();
    hidePopup();
  }, [refreshPatientContext, hidePopup]);

  const handleEditClick = useCallback(() => {
    showPopup(
      <PersonalInformationEditModal
        modalType={ModalModalityEnum.Edit}
        initialData={patientData}
        onSave={handlePersonalInfoEdit}
      />
    );
  }, [showPopup, patientData, handlePersonalInfoEdit]);

  const memoizedPatientData = useMemo(() => {
    if (!patientData) return null;
    return {
      name: `${patientData.first_name || ""} ${
        patientData.last_name || ""
      }`.trim(),
      dateOfBirth:
        formatDisplayDate(patientData.birthday, {
          locale: dateSettings?.locale,
          format: dateSettings?.format,
        }) || "-",
      sex: patientData.gender || "-",
      age: patientData.age != null ? patientData.age.toString() : "-",
      nationality: patientData.nationality || "-",
      address: patientData.address
        ? `${patientData.address.street || ""}, ${
            patientData.address.city || ""
          }`
        : "-",
      city: patientData.address ? patientData.address.city || "-" : "-",
      country: patientData.address ? patientData.address.country || "-" : "-",
      email: patientData.email || "-",
      phone: patientData.phone || "-",
    };
  }, [patientData, dateSettings]);

  if (patientLoading)
    return (
      <div>
        <WidgetLoader />
      </div>
    );
  if (patientError)
    return (
      <div>
        {t("error")}: {patientError.message}
      </div>
    );
  if (!memoizedPatientData)
    return (
      <div>
        <WidgetLoader />
      </div>
    );

  return (
    <div className="personal-information-container">
      <div className="edit-icon-wrapper">
        {canEdit && (
          <IconButton
            icon={IconsEnum.EDIT}
            onClick={handleEditClick}
            className="edit-icon"
            noBgColor
            title={t("208")}
          />
        )}
      </div>
      <div className="info-details-container">
        <div className="name-details">
          <div className="name App-title-widgets">
            {`${memoizedPatientData.name}, ${memoizedPatientData.age}`}
          </div>
          <InfoItem label={t("8")} value={memoizedPatientData.dateOfBirth} />
          <InfoItem label={t("9")} value={memoizedPatientData.sex} />
        </div>

        <div className="address-info">
          <InfoItem label={t("84")} value={memoizedPatientData.nationality} />
          <InfoItem label={t("85")} value={memoizedPatientData.address} />
          <InfoItem label={t("11")} value={memoizedPatientData.city} />
          <InfoItem label={t("14")} value={memoizedPatientData.country} />
        </div>

        <div className="contacts-info">
          <InfoItem label={t("87")} value={memoizedPatientData.email} />
          <InfoItem label={t("88")} value={memoizedPatientData.phone} />
        </div>
      </div>
    </div>
  );
});

export default PersonalInformation;
